/**
 * Texture Cache System for WebGL Node Rendering
 *
 * Phase 0.7: Texture caching integration with SnapDOM for HTML-to-texture conversion.
 *
 * Features:
 * - Captures HTML elements to textures using SnapDOM
 * - LRU eviction when memory limit is reached
 * - Texture invalidation on content change
 * - Memory tracking
 *
 * Note: Phase 0.8 adds actual texture generation pipeline.
 * Note: Phase 0.11 moves generation to Web Worker (will require refactoring SnapDOM usage).
 */

import { snapdom } from '@zumer/snapdom';

/** Texture entry in the cache */
export interface CachedTexture {
	/** WebGL texture object */
	texture: WebGLTexture;
	/** Texture width in pixels */
	width: number;
	/** Texture height in pixels */
	height: number;
	/** Estimated memory usage in bytes */
	memoryBytes: number;
	/** Last access timestamp for LRU */
	lastAccess: number;
	/** Content hash for invalidation */
	contentHash: string;
}

/** Texture generation request */
export interface TextureRequest {
	nodeId: string;
	/** Callback that returns the HTML element to capture */
	getElement: () => HTMLElement | null;
	/** Content hash for cache invalidation */
	contentHash: string;
	/** Priority (higher = sooner) */
	priority: number;
	/** Expected LOD level at capture time (for validation) */
	expectedLOD?: string;
	/** Target size in pixels (max dimension). Texture will be resized to approximately this size. */
	targetSize?: number;
}

/** Texture cache statistics */
export interface TextureCacheStats {
	/** Number of cached textures */
	count: number;
	/** Total memory usage in bytes */
	memoryBytes: number;
	/** Memory limit in bytes */
	memoryLimit: number;
	/** Number of cache hits this session */
	hits: number;
	/** Number of cache misses this session */
	misses: number;
	/** Number of pending texture requests */
	pendingRequests: number;
}

/** Skip reason for debug tracking */
export type SkipReason = 'lod_mismatch' | 'element_missing' | 'already_cached' | 'invalidated' | 'capture_failed' | 'images_not_loaded' | 'blank_texture';

/** Debug state for texture cache visualization */
export interface TextureCacheDebugState {
	/** IDs of nodes with pending texture requests */
	pendingIds: string[];
	/** IDs of nodes with cached textures */
	cachedIds: string[];
	/** ID of node currently being processed (null if idle) */
	currentlyProcessing: string | null;
	/** Recently skipped nodes with reason and timestamp */
	recentSkips: Array<{ nodeId: string; reason: SkipReason; timestamp: number }>;
	/** Queue length */
	queueLength: number;
	/** Whether processing is active */
	isProcessing: boolean;
	/** Stats */
	stats: TextureCacheStats;
	/** Textures generated this session */
	texturesGenerated: number;
	/** Textures skipped this session */
	texturesSkipped: number;
}

/** Options for texture cache */
export interface TextureCacheOptions {
	/** Memory limit in bytes (default: 256MB) */
	memoryLimit?: number;
	/** Scale factor for texture capture (default: 1 - no DPR scaling for textures) */
	scale?: number;
	/** Maximum texture dimension (default: 2048) */
	maxTextureSize?: number;
	/**
	 * Resolution multiplier for texture target size (default: 1.0).
	 * Final texture size = targetSize * textureResolutionMultiplier.
	 * Values < 1.0 reduce texture resolution (saves memory, faster).
	 * Values > 1.0 increase texture resolution (sharper, more memory).
	 */
	textureResolutionMultiplier?: number;
	/**
	 * Default target size when not specified in request (default: 150).
	 * This is the max dimension the texture will be resized to.
	 */
	defaultTargetSize?: number;
	/**
	 * Callback when textures are ready for rendering.
	 * Called at most once per frame, coalesced across all texture completions.
	 * Use this to trigger canvas invalidation/re-render.
	 */
	onTexturesReady?: () => void;
}

const DEFAULT_OPTIONS: Required<Omit<TextureCacheOptions, 'onTexturesReady'>> & {
	onTexturesReady: (() => void) | undefined;
} = {
	memoryLimit: 1024 * 1024 * 1024, // 1GB - generous for modern GPUs
	scale: 1, // No DPR scaling - textures are sized by targetSize
	maxTextureSize: 2048,
	textureResolutionMultiplier: 1.0,
	defaultTargetSize: 150,
	onTexturesReady: undefined
};

/**
 * TextureCache manages WebGL textures for node rendering.
 *
 * Usage:
 * 1. Check if texture exists: `hasTexture(nodeId)`
 * 2. Request texture generation: `requestTexture(request)`
 * 3. Get texture for rendering: `getTexture(nodeId)`
 * 4. Invalidate on content change: `invalidate(nodeId)`
 */
export class TextureCache {
	private gl: WebGL2RenderingContext;
	private options: typeof DEFAULT_OPTIONS;

	// Cache storage
	private textures: Map<string, CachedTexture> = new Map();
	private pendingRequests: Map<string, TextureRequest> = new Map();

	// Statistics
	private stats = {
		hits: 0,
		misses: 0,
		totalMemory: 0
	};

	// Generation queue (for batching)
	private generationQueue: TextureRequest[] = [];
	private isProcessing: boolean = false;

	// Debug tracking
	private currentlyProcessing: string | null = null;
	private recentSkips: Array<{ nodeId: string; reason: SkipReason; timestamp: number }> = [];
	private texturesGenerated: number = 0;
	private texturesSkipped: number = 0;
	private readonly SKIP_RETENTION_MS = 5000; // Keep skip records for 5 seconds

	// Frame-coalesced callback for texture ready notification
	private pendingReadyCallback: boolean = false;

	constructor(gl: WebGL2RenderingContext, options?: TextureCacheOptions) {
		this.gl = gl;
		this.options = { ...DEFAULT_OPTIONS, ...options };
	}

	/**
	 * Signal that textures are ready for rendering.
	 * Uses requestAnimationFrame to coalesce multiple completions into at most one callback per frame.
	 */
	private signalTexturesReady(): void {
		if (!this.options.onTexturesReady || this.pendingReadyCallback) {
			return;
		}

		this.pendingReadyCallback = true;
		requestAnimationFrame(() => {
			this.pendingReadyCallback = false;
			this.options.onTexturesReady?.();
		});
	}

	/**
	 * Check if a texture exists for a node.
	 */
	hasTexture(nodeId: string): boolean {
		return this.textures.has(nodeId);
	}

	/**
	 * Check if a texture exists and matches the content hash.
	 */
	hasValidTexture(nodeId: string, contentHash: string): boolean {
		const cached = this.textures.get(nodeId);
		return cached !== undefined && cached.contentHash === contentHash;
	}

	/**
	 * Get a texture for rendering.
	 * Updates last access time for LRU.
	 */
	getTexture(nodeId: string): CachedTexture | null {
		const cached = this.textures.get(nodeId);
		if (cached) {
			cached.lastAccess = performance.now();
			this.stats.hits++;
			return cached;
		}
		this.stats.misses++;
		return null;
	}

	/**
	 * Request texture generation for a node.
	 * The texture will be generated asynchronously.
	 */
	requestTexture(request: TextureRequest): void {
		// Skip if already cached with same hash
		if (this.hasValidTexture(request.nodeId, request.contentHash)) {
			return;
		}

		// Skip if already pending with same hash
		const pending = this.pendingRequests.get(request.nodeId);
		if (pending && pending.contentHash === request.contentHash) {
			return;
		}

		// Add to pending
		this.pendingRequests.set(request.nodeId, request);
		this.generationQueue.push(request);

		// Sort by priority (higher first)
		this.generationQueue.sort((a, b) => b.priority - a.priority);

		// Start processing if not already
		this.processQueue();
	}

	/**
	 * Invalidate a cached texture.
	 */
	invalidate(nodeId: string): void {
		const cached = this.textures.get(nodeId);
		if (cached) {
			this.gl.deleteTexture(cached.texture);
			this.stats.totalMemory -= cached.memoryBytes;
			this.textures.delete(nodeId);
		}
		this.pendingRequests.delete(nodeId);
	}

	/**
	 * Invalidate all cached textures.
	 */
	invalidateAll(): void {
		for (const [nodeId, cached] of this.textures) {
			this.gl.deleteTexture(cached.texture);
		}
		this.textures.clear();
		this.pendingRequests.clear();
		this.generationQueue = [];
		this.stats.totalMemory = 0;
	}

	/**
	 * Get cache statistics.
	 */
	getStats(): TextureCacheStats {
		return {
			count: this.textures.size,
			memoryBytes: this.stats.totalMemory,
			memoryLimit: this.options.memoryLimit,
			hits: this.stats.hits,
			misses: this.stats.misses,
			pendingRequests: this.pendingRequests.size
		};
	}

	/**
	 * Get current texture resolution multiplier.
	 */
	getTextureResolutionMultiplier(): number {
		return this.options.textureResolutionMultiplier;
	}

	/**
	 * Set texture resolution multiplier.
	 * Note: This does not invalidate existing textures. Call invalidateAll() if needed.
	 */
	setTextureResolutionMultiplier(multiplier: number): void {
		this.options.textureResolutionMultiplier = Math.max(0.1, Math.min(4.0, multiplier));
	}

	/**
	 * Get default target size for textures.
	 */
	getDefaultTargetSize(): number {
		return this.options.defaultTargetSize;
	}

	/**
	 * Set default target size for textures.
	 * Note: This does not invalidate existing textures. Call invalidateAll() if needed.
	 */
	setDefaultTargetSize(size: number): void {
		this.options.defaultTargetSize = Math.max(32, Math.min(1024, size));
	}

	/**
	 * Get all current options.
	 */
	getOptions(): typeof DEFAULT_OPTIONS {
		return { ...this.options };
	}

	/**
	 * Update options. Does not invalidate existing textures.
	 */
	setOptions(options: Partial<TextureCacheOptions>): void {
		this.options = { ...this.options, ...options };
	}

	/**
	 * Get debug state for visualization.
	 */
	getDebugState(): TextureCacheDebugState {
		// Clean up old skips
		const now = performance.now();
		this.recentSkips = this.recentSkips.filter(
			(skip) => now - skip.timestamp < this.SKIP_RETENTION_MS
		);

		return {
			pendingIds: Array.from(this.pendingRequests.keys()),
			cachedIds: Array.from(this.textures.keys()),
			currentlyProcessing: this.currentlyProcessing,
			recentSkips: [...this.recentSkips],
			queueLength: this.generationQueue.length,
			isProcessing: this.isProcessing,
			stats: this.getStats(),
			texturesGenerated: this.texturesGenerated,
			texturesSkipped: this.texturesSkipped
		};
	}

	/**
	 * Check if a node is pending texture generation.
	 */
	isPending(nodeId: string): boolean {
		return this.pendingRequests.has(nodeId);
	}

	/**
	 * Get texture preview thumbnails for inspection.
	 * Returns an array of preview objects with nodeId, width, height, and dataUrl.
	 * @param maxCount Maximum number of previews to return (default: 20)
	 * @param thumbnailSize Maximum dimension for thumbnails (default: 64)
	 */
	getTexturePreviews(
		maxCount: number = 20,
		thumbnailSize: number = 64
	): Array<{
		nodeId: string;
		width: number;
		height: number;
		memoryBytes: number;
		dataUrl: string;
	}> {
		const previews: Array<{
			nodeId: string;
			width: number;
			height: number;
			memoryBytes: number;
			dataUrl: string;
		}> = [];

		// Create a temporary canvas for reading textures
		const tempCanvas = document.createElement('canvas');
		const tempCtx = tempCanvas.getContext('2d');
		if (!tempCtx) return previews;

		// Create a framebuffer to read from WebGL textures
		const framebuffer = this.gl.createFramebuffer();
		if (!framebuffer) return previews;

		let count = 0;
		for (const [nodeId, cached] of this.textures) {
			if (count >= maxCount) break;

			try {
				// Bind framebuffer and attach texture
				this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, framebuffer);
				this.gl.framebufferTexture2D(
					this.gl.FRAMEBUFFER,
					this.gl.COLOR_ATTACHMENT0,
					this.gl.TEXTURE_2D,
					cached.texture,
					0
				);

				// Check framebuffer status
				if (this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER) !== this.gl.FRAMEBUFFER_COMPLETE) {
					continue;
				}

				// Read pixels from framebuffer
				const pixels = new Uint8Array(cached.width * cached.height * 4);
				this.gl.readPixels(
					0,
					0,
					cached.width,
					cached.height,
					this.gl.RGBA,
					this.gl.UNSIGNED_BYTE,
					pixels
				);

				// Create ImageData (flip Y since WebGL has different origin)
				const imageData = new ImageData(cached.width, cached.height);
				for (let y = 0; y < cached.height; y++) {
					const srcRow = (cached.height - 1 - y) * cached.width * 4;
					const dstRow = y * cached.width * 4;
					for (let x = 0; x < cached.width * 4; x++) {
						imageData.data[dstRow + x] = pixels[srcRow + x];
					}
				}

				// Calculate thumbnail size maintaining aspect ratio
				const scale = Math.min(thumbnailSize / cached.width, thumbnailSize / cached.height, 1);
				const thumbWidth = Math.max(1, Math.floor(cached.width * scale));
				const thumbHeight = Math.max(1, Math.floor(cached.height * scale));

				// Draw to temp canvas at thumbnail size
				tempCanvas.width = thumbWidth;
				tempCanvas.height = thumbHeight;

				// First draw full size to get ImageData, then scale
				const fullCanvas = document.createElement('canvas');
				fullCanvas.width = cached.width;
				fullCanvas.height = cached.height;
				const fullCtx = fullCanvas.getContext('2d');
				if (fullCtx) {
					fullCtx.putImageData(imageData, 0, 0);
					tempCtx.drawImage(fullCanvas, 0, 0, thumbWidth, thumbHeight);
				}

				// Get data URL
				const dataUrl = tempCanvas.toDataURL('image/png');

				previews.push({
					nodeId,
					width: cached.width,
					height: cached.height,
					memoryBytes: cached.memoryBytes,
					dataUrl
				});

				count++;
			} catch (e) {
				// Skip textures that fail to read
				console.warn(`Failed to read texture preview for ${nodeId}:`, e);
			}
		}

		// Clean up
		this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
		this.gl.deleteFramebuffer(framebuffer);

		return previews;
	}

	/**
	 * Check if a node was recently skipped.
	 */
	wasRecentlySkipped(nodeId: string): boolean {
		const now = performance.now();
		return this.recentSkips.some(
			(skip) => skip.nodeId === nodeId && now - skip.timestamp < this.SKIP_RETENTION_MS
		);
	}

	/**
	 * Record a skip for debug tracking.
	 */
	private recordSkip(nodeId: string, reason: SkipReason): void {
		this.texturesSkipped++;
		this.recentSkips.push({
			nodeId,
			reason,
			timestamp: performance.now()
		});
		// Keep only last 100 skips to prevent memory growth
		if (this.recentSkips.length > 100) {
			this.recentSkips = this.recentSkips.slice(-100);
		}
	}

	/**
	 * Check if a canvas has any non-transparent content.
	 * Samples pixels to detect blank/transparent textures efficiently.
	 */
	private canvasHasContent(canvas: HTMLCanvasElement): boolean {
		const width = canvas.width;
		const height = canvas.height;
		if (width === 0 || height === 0) return false;

		const ctx = canvas.getContext('2d');
		if (!ctx) return false;

		// Get all pixel data in one call (single GPU->CPU transfer)
		// This is faster than multiple getImageData calls for individual pixels
		const imageData = ctx.getImageData(0, 0, width, height);
		const data = imageData.data;

		// Helper to check alpha at a given coordinate
		const hasContentAt = (x: number, y: number): boolean => {
			const alphaIndex = (y * width + x) * 4 + 3;
			return data[alphaIndex] > 0;
		};

		// Sample corners, center, and quarter points
		const samplePoints = [
			[0, 0], // top-left
			[width - 1, 0], // top-right
			[0, height - 1], // bottom-left
			[width - 1, height - 1], // bottom-right
			[Math.floor(width / 2), Math.floor(height / 2)], // center
			[Math.floor(width / 4), Math.floor(height / 4)], // quarter points
			[Math.floor(width * 3 / 4), Math.floor(height / 4)],
			[Math.floor(width / 4), Math.floor(height * 3 / 4)],
			[Math.floor(width * 3 / 4), Math.floor(height * 3 / 4)],
		];

		for (const [x, y] of samplePoints) {
			if (hasContentAt(x, y)) {
				return true;
			}
		}

		// If all sample points are transparent, do a more thorough check
		// by sampling along a diagonal
		const step = Math.max(1, Math.floor(Math.min(width, height) / 20));
		for (let i = 0; i < Math.min(width, height); i += step) {
			if (hasContentAt(i, i)) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Process the generation queue using idle callbacks for better performance.
	 * Uses requestIdleCallback when available to process textures during browser idle time,
	 * allowing batch processing of multiple textures per frame when the browser isn't busy.
	 */
	private processQueue(): void {
		if (this.isProcessing || this.generationQueue.length === 0) {
			return;
		}

		this.isProcessing = true;
		this.scheduleNextBatch();
	}

	/** Maximum textures to process per batch (keep small to avoid blocking) */
	private readonly BATCH_SIZE = 3;

	/**
	 * Schedule the next batch of texture processing.
	 * Uses setTimeout to process batches without blocking the main thread.
	 */
	private scheduleNextBatch(): void {
		if (this.generationQueue.length === 0) {
			this.isProcessing = false;
			this.currentlyProcessing = null;
			return;
		}

		// Use setTimeout(0) to yield to other work between batches
		setTimeout(() => this.processNextBatch(), 0);
	}

	/**
	 * Process the next batch of textures.
	 */
	private processNextBatch(): void {
		const batchSize = Math.min(this.BATCH_SIZE, this.generationQueue.length);
		this.processBatchItem(batchSize);
	}

	/**
	 * Process batch items recursively with Promise chain.
	 */
	private processBatchItem(remaining: number): void {
		if (remaining <= 0 || this.generationQueue.length === 0) {
			this.currentlyProcessing = null;
			this.scheduleNextBatch();
			return;
		}

		const request = this.generationQueue.shift()!;

		// Skip if no longer pending (was invalidated)
		if (!this.pendingRequests.has(request.nodeId)) {
			this.recordSkip(request.nodeId, 'invalidated');
			// Continue without decrementing remaining (skip doesn't count)
			this.processBatchItem(remaining);
			return;
		}

		// Skip if already cached with same hash
		if (this.hasValidTexture(request.nodeId, request.contentHash)) {
			this.pendingRequests.delete(request.nodeId);
			this.recordSkip(request.nodeId, 'already_cached');
			this.processBatchItem(remaining);
			return;
		}

		// Track currently processing for debug
		this.currentlyProcessing = request.nodeId;

		// Generate texture then continue with next item
		this.generateTexture(request).then(() => {
			this.processBatchItem(remaining - 1);
		});
	}

	/**
	 * Generate a texture for a request.
	 */
	private async generateTexture(request: TextureRequest): Promise<void> {
		const element = request.getElement();
		if (!element) {
			this.pendingRequests.delete(request.nodeId);
			this.recordSkip(request.nodeId, 'element_missing');
			return;
		}

		// Validate LOD hasn't changed since request was made (race condition guard)
		if (request.expectedLOD) {
			const currentLOD = element.getAttribute('data-lod');
			if (currentLOD !== request.expectedLOD) {
				// LOD changed since request - skip capture, new request will be made
				this.pendingRequests.delete(request.nodeId);
				this.recordSkip(request.nodeId, 'lod_mismatch');
				return;
			}
		}

		// Validate all images are loaded before capturing
		// This prevents blank textures from being cached when images haven't loaded yet
		const images = element.querySelectorAll('img');
		let hasUnloadedImages = false;
		for (const img of images) {
			if (!img.complete || img.naturalHeight === 0) {
				hasUnloadedImages = true;
				break;
			}
		}

		if (hasUnloadedImages) {
			// Image not loaded - don't cache a blank texture
			// Remove from pending so a new request can be made on next render
			this.pendingRequests.delete(request.nodeId);
			this.recordSkip(request.nodeId, 'images_not_loaded');
			return;
		}

		// If there are images, wait for a paint cycle to ensure they're rendered to DOM
		// This handles the case where img.complete is true but the browser hasn't painted yet
		if (images.length > 0) {
			await new Promise<void>(resolve => {
				requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
			});

			// Re-check LOD after waiting (it might have changed)
			if (request.expectedLOD) {
				const currentLOD = element.getAttribute('data-lod');
				if (currentLOD !== request.expectedLOD) {
					this.pendingRequests.delete(request.nodeId);
					this.recordSkip(request.nodeId, 'lod_mismatch');
					return;
				}
			}

			// Re-check images are still loaded
			for (const img of images) {
				if (!img.complete || img.naturalHeight === 0) {
					this.pendingRequests.delete(request.nodeId);
					this.recordSkip(request.nodeId, 'images_not_loaded');
					return;
				}
			}
		}

		try {
			// Temporarily remove rotation transform before capture
			// This allows rotation to be applied via shader (nearly free) instead of
			// baking it into the texture (expensive and requires recapture on rotation change)
			const savedTransform = element.style.transform;
			element.style.transform = '';

			// Use SnapDOM to capture the element (now without rotation)
			const result = await snapdom(element, {
				scale: this.options.scale,
				outerTransforms: false
			});

			// Restore the rotation transform
			element.style.transform = savedTransform;

			// Convert to canvas
			const capturedCanvas = await result.toCanvas();

			// Validate the captured canvas has actual content (not blank/transparent)
			// This prevents caching textures that would render as invisible
			if (!this.canvasHasContent(capturedCanvas)) {
				this.pendingRequests.delete(request.nodeId);
				this.recordSkip(request.nodeId, 'blank_texture');
				return;
			}

			// Calculate target size for this texture
			// If request specifies targetSize, use that; otherwise use default
			const baseTargetSize = request.targetSize ?? this.options.defaultTargetSize;
			const targetSize = Math.round(baseTargetSize * this.options.textureResolutionMultiplier);

			// Limit by max texture size
			const maxSize = Math.min(
				this.options.maxTextureSize,
				this.gl.getParameter(this.gl.MAX_TEXTURE_SIZE)
			);

			let width = capturedCanvas.width;
			let height = capturedCanvas.height;

			// Calculate the scale factor to resize to target size
			// We resize based on the max dimension to maintain aspect ratio
			const maxDimension = Math.max(width, height);
			const effectiveTargetSize = Math.min(targetSize, maxSize);

			// Only resize if the captured canvas is larger than target
			// (or if it exceeds max size)
			if (maxDimension > effectiveTargetSize) {
				const scale = effectiveTargetSize / maxDimension;
				width = Math.max(1, Math.floor(width * scale));
				height = Math.max(1, Math.floor(height * scale));

				// Resize canvas
				const resizedCanvas = document.createElement('canvas');
				resizedCanvas.width = width;
				resizedCanvas.height = height;
				const ctx = resizedCanvas.getContext('2d')!;
				ctx.drawImage(capturedCanvas, 0, 0, width, height);

				// Use resized canvas for texture
				this.uploadTexture(request.nodeId, request.contentHash, resizedCanvas, width, height);
			} else {
				this.uploadTexture(request.nodeId, request.contentHash, capturedCanvas, width, height);
			}

			// Track successful generation
			this.texturesGenerated++;
		} catch (error) {
			console.warn(`Failed to generate texture for ${request.nodeId}:`, error);
			this.recordSkip(request.nodeId, 'capture_failed');
		} finally {
			this.pendingRequests.delete(request.nodeId);
		}
	}

	/**
	 * Upload a canvas to a WebGL texture.
	 */
	private uploadTexture(
		nodeId: string,
		contentHash: string,
		canvas: HTMLCanvasElement,
		width: number,
		height: number
	): void {
		// Evict if needed to stay under memory limit
		const memoryNeeded = width * height * 4; // RGBA
		this.evictIfNeeded(memoryNeeded);

		// Delete existing texture if any
		this.invalidate(nodeId);

		// Create WebGL texture
		const texture = this.gl.createTexture();
		if (!texture) {
			console.warn(`Failed to create texture for ${nodeId}`);
			return;
		}

		this.gl.bindTexture(this.gl.TEXTURE_2D, texture);

		// Set texture parameters
		this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
		this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
		this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
		this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);

		// Upload canvas to texture
		this.gl.texImage2D(
			this.gl.TEXTURE_2D,
			0,
			this.gl.RGBA,
			this.gl.RGBA,
			this.gl.UNSIGNED_BYTE,
			canvas
		);

		this.gl.bindTexture(this.gl.TEXTURE_2D, null);

		// Store in cache
		const cached: CachedTexture = {
			texture,
			width,
			height,
			memoryBytes: memoryNeeded,
			lastAccess: performance.now(),
			contentHash
		};

		this.textures.set(nodeId, cached);
		this.stats.totalMemory += memoryNeeded;

		// Signal that a texture is ready (coalesced to at most once per frame)
		this.signalTexturesReady();
	}

	/**
	 * Evict textures if needed to stay under memory limit.
	 */
	private evictIfNeeded(additionalBytes: number): void {
		const targetMemory = this.options.memoryLimit - additionalBytes;

		if (this.stats.totalMemory <= targetMemory) {
			return;
		}

		// Sort by last access (oldest first)
		const entries = Array.from(this.textures.entries()).sort(
			(a, b) => a[1].lastAccess - b[1].lastAccess
		);

		// Evict until under limit
		for (const [nodeId, cached] of entries) {
			if (this.stats.totalMemory <= targetMemory) {
				break;
			}

			this.gl.deleteTexture(cached.texture);
			this.stats.totalMemory -= cached.memoryBytes;
			this.textures.delete(nodeId);
		}
	}

	/**
	 * Clean up all resources.
	 */
	destroy(): void {
		this.invalidateAll();
	}
}

/**
 * Generate a simple content hash for cache invalidation.
 * Uses a fast, non-cryptographic hash.
 */
export function hashContent(content: string): string {
	let hash = 0;
	for (let i = 0; i < content.length; i++) {
		const char = content.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash = hash & hash; // Convert to 32-bit integer
	}
	return hash.toString(36);
}
